import { Component, inject, OnInit, OnDestroy, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter } from 'rxjs';
import { provideIcons } from '@ng-icons/core';
import {
  lucideBriefcase,
  lucideChevronDown,
  lucideUser,
  lucideWrench,
} from '@ng-icons/lucide';
import { HlmButton, HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmNavigationMenuImports } from '@spartan-ng/helm/navigation-menu';
import { TranslocoDirective } from '@jsverse/transloco';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { HlmTooltipImports } from '@spartan-ng/helm/tooltip';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    RouterLink,
    RouterLinkActive,
    HlmButtonImports,
    HlmNavigationMenuImports,
    HlmIconImports,
    HlmTypographyImports,
    HlmTooltipImports,
    TranslocoDirective,
  ],
  providers: [
    provideIcons({
      lucideChevronDown,
      lucideUser,
      lucideBriefcase,
      lucideWrench,
    }),
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.css',
})
export class HeaderComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);
  private subscription: ReturnType<typeof Router.prototype.events.subscribe> | null = null;

  protected mobileOpen = false;
  /** True when current route is any platform child (overview, smart-locks, etc.) */
  protected isPlatformActive = signal(false);

  ngOnInit(): void {
    this.updatePlatformActive();
    this.subscription = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.updatePlatformActive());
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.setBodyScroll(true);
  }

  private updatePlatformActive(): void {
    const url = this.router.url;
    this.isPlatformActive.set(url.includes('platform/') || url.includes('platform'));
  }

  protected closeMobile(): void {
    this.mobileOpen = false;
    this.setBodyScroll(true);
  }

  protected toggleMobile(): void {
    this.mobileOpen = !this.mobileOpen;
    this.setBodyScroll(!this.mobileOpen);
  }

  private setBodyScroll(enabled: boolean): void {
    if (isPlatformBrowser(this.platformId)) {
      document.body.style.overflow = enabled ? '' : 'hidden';
    }
  }
}
